import { Link } from 'react-router-dom';
import { getImageUrl } from '../../../utils/config';
import { formatPrice } from '../../../utils/currency';

const ProductCard = ({ 
  product, 
  isAdmin = false, 
  onEdit = null, 
  onDelete = null,
  onToggleStatus = null,
  categoryId = null,
  store = null
}) => {
  const handleEdit = (e) => {
    e.preventDefault();
    if (onEdit) onEdit(product);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    if (onDelete) onDelete(product);
  };

  const handleToggleStatus = (e) => {
    e.preventDefault();
    if (onToggleStatus) onToggleStatus(product);
  };

  const calculateDiscountedPrice = () => {
    const price = parseFloat(product.price) || 0;
    const discount = parseFloat(product.discount_percentage) || 0;
    
    if (discount > 0) {
      return price - (price * discount / 100);
    }
    return price;
  };

  const cardContent = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
      {/* Product Image */}
      <div className="relative">
        {product.image ? (
          <img
            src={getImageUrl(product.image)}
            alt={product.name}
            className="w-full h-48 object-cover rounded-t-xl"
            onError={e => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              e.target.parentNode.innerHTML = `<div class="w-full h-48 bg-gray-100 rounded-t-xl flex items-center justify-center"><span class="text-gray-400 text-4xl">📦</span></div>`;
            }}
          />
        ) : (
          <div className="w-full h-48 bg-gray-100 rounded-t-xl flex items-center justify-center">
            <span className="text-gray-400 text-4xl">📦</span>
          </div>
        )}
        
        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            product.status === 'active' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {product.status}
          </span>
        </div>

        {/* Discount Badge */}
        {product.discount_percentage > 0 && (
          <div className="absolute top-2 left-2">
            <span className="px-2 py-1 bg-red-500 text-white rounded-full text-xs font-medium">
              -{product.discount_percentage}%
            </span>
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
          {product.name}
        </h3>
        
        {product.details && (
          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
            {product.details}
          </p>
        )}

        {/* Price */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {parseFloat(product.discount_percentage) > 0 ? (
              <>
                <span className="text-lg font-bold text-green-600">
                  {formatPrice(calculateDiscountedPrice(), store?.currency)}
                </span>
                <span className="text-sm text-gray-500 line-through">
                  {formatPrice(product.price, store?.currency)}
                </span>
              </>
            ) : (
              <span className="text-lg font-bold text-gray-900">
                {formatPrice(product.price, store?.currency)}
              </span>
            )}
          </div>
          
          {product.category && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {product.category.name}
            </span>
          )}
        </div>

        {/* Admin Actions */}
        {isAdmin && (
          <div className="border-t border-gray-100 pt-3">
            <div className="flex gap-2">
              <button
                onClick={handleEdit}
                className="flex-1 px-3 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
              >
                Edit
              </button>
              <button
                onClick={handleToggleStatus}
                className={`flex-1 px-3 py-2 text-sm rounded-lg transition-colors ${
                  product.status === 'active'
                    ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50'
                    : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                }`}
              >
                {product.status === 'active' ? 'Deactivate' : 'Activate'}
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-3 py-2 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // If it's a public view and we have a categoryId, make it clickable
  if (!isAdmin && categoryId) {
    return (
      <Link 
        to={`/categories/${categoryId}/products/${product.id}`}
        className="block hover:no-underline"
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
};

export default ProductCard; 